import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function VisitsChart({
  userId,
  // override any of these via props if you want different hues
  colors = {
    sell: "#7C3AED", // purple-600
    rent: "#10B981", // emerald-500
    grid: "#E5E7EB", // gray-200
    axis: "#6B7280", // gray-500
    tooltipBg: "#FFFFFF",
  },
}) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartTitle, setChartTitle] = useState("");
  const [viewMode, setViewMode] = useState("sell"); // "sell" | "rent"

  const formatMonthMMM = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return new Intl.DateTimeFormat("en-US", { month: "short" }).format(d);
  };

  // Generate last 12 months (YYYY-MM) ending at `lastMonth` (YYYY-MM)
  const generateLast12Months = (lastMonth) => {
    const [y, m] = lastMonth.split("-").map(Number);
    const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(y, m - 1 - (11 - i), 1); // oldest -> newest
      out.push(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      );
    }
    return out;
  };

  const formatValueAbbrev = (v) => {
    if (v >= 1_000_000)
      return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
    return String(v);
  };

  const formatCurrency = (v) => {
    try {
      return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
    } catch {
      return String(v);
    }
  };

  // fetch and shape data
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        setChartData(null);

        if (!userId) {
          throw new Error("User ID is missing. Unable to fetch data.");
        }

        const url = `${
          import.meta.env.VITE_BACKEND_URL
        }/dash/dashboard-insights/total-listings-value`;
        const res = await axios.get(url, { params: { proSlug: userId } });

        if (!res?.data?.success) {
          throw new Error(
            res?.data?.message || "Failed to fetch dashboard insights."
          );
        }

        const sellPriceData = Array.isArray(res.data.sellPriceData)
          ? res.data.sellPriceData
          : [];
        const rentPriceData = Array.isArray(res.data.rentPriceData)
          ? res.data.rentPriceData
          : [];

        if (sellPriceData.length === 0 && rentPriceData.length === 0) {
          setChartTitle("");
          setChartData([]);
          return;
        }

        // find latest month present in either series
        const allMonths = [...sellPriceData, ...rentPriceData]
          .map((it) => it?.month)
          .filter(Boolean);

        // fallback: if API returns odd shapes
        const latestYm = allMonths.sort().slice(-1)[0]; // lexicographic works for YYYY-MM
        const last12 = generateLast12Months(latestYm);

        // title like "2024-2025" if it spans years
        const years = [...new Set(last12.map((ym) => ym.split("-")[0]))];
        if (!cancelled) setChartTitle(years.join("-"));

        // index by month for O(1) lookups
        const sellByMonth = Object.fromEntries(
          sellPriceData.map((it) => [it.month, Number(it.totalSellValue || 0)])
        );
        const rentByMonth = Object.fromEntries(
          rentPriceData.map((it) => [it.month, Number(it.totalRentValue || 0)])
        );

        const shaped = last12.map((ym) => ({
          ym,
          name: formatMonthMMM(ym),
          sellValue: sellByMonth[ym] ?? 0,
          rentValue: rentByMonth[ym] ?? 0,
        }));

        if (!cancelled) setChartData(shaped);
      } catch (e) {
        if (!cancelled)
          setError(e.message || "An error occurred while fetching data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleToggleChange = (_e, v) => {
    if (v) setViewMode(v);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box
        height={300}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body2" color="text.secondary">
          No data for the past 12 months.
        </Typography>
      </Box>
    );
  }

  const stroke = viewMode === "sell" ? colors.sell : colors.rent;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {chartTitle} — {viewMode === "sell" ? "Sell Value" : "Rent Value"}
      </Typography>

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleToggleChange}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="sell">Sell</ToggleButton>
        <ToggleButton value="rent">Rent</ToggleButton>
      </ToggleButtonGroup>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis dataKey="name" stroke={colors.axis} />
          <YAxis tickFormatter={formatValueAbbrev} stroke={colors.axis} />
          <Tooltip
            formatter={(v) => [
              `$${formatCurrency(v)}`,
              viewMode === "sell" ? "Sell Value" : "Rent Value",
            ]}
            contentStyle={{ borderRadius: 8, background: colors.tooltipBg }}
            cursor={{ strokeDasharray: "3 3" }}
          />
          <Line
            type="monotone"
            dataKey={viewMode === "sell" ? "sellValue" : "rentValue"}
            name={viewMode === "sell" ? "Sell Value" : "Rent Value"}
            stroke={stroke}
            dot={{ r: 3 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default VisitsChart;
